// src/pages/Visitors/VisitorLogDashboard.tsx
//
// Visitor log admin screen. Two views over the same table:
//   Today   → GET /visitors/today    (session + date scoped by the backend,
//                                     returns status counts)
//   History → GET /visitors/history  (requires session_id, date range and
//                                     type filters, no counts)
//
// Row actions: view (GET /:id), edit (PUT /:id), check out
// (PUT /:id/check-out), delete (DELETE /:id).

import React, { useCallback, useEffect, useRef, useState, JSX } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../../components/CustomPaginator';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import VisitorFormModal from './parts/VisitorFormModal';
import ViewVisitorModal from './parts/ViewVisitorModal';
import VisitorLogSkeletonLoader from '../../skeletons/VisitorLogSkeletonLoader';
import {
  ApiVisitorLog,
  MEET_PERSON_TYPE_OPTIONS,
  STATUS_META,
  VISITOR_TYPE_OPTIONS,
  VisitorPagination,
  VisitorRecord,
  VisitorStatus,
  VisitorStatusCounts,
  formatTime,
  formatVisitDate,
  getInitials,
  mapApiVisitor,
  toDateParam,
} from './parts/types';
import { GlobalDateField } from '../../components/GlobalComponents/GlobalDateField';
import { GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';

type TabKey = 'today' | 'history';
type StatusFilter = 'all' | VisitorStatus;

const EMPTY_COUNTS: VisitorStatusCounts = { checked_in: 0, checked_out: 0, denied: 0 };

const STATUS_FILTERS: { value: StatusFilter; label: string; color: string }[] = [
  { value: 'all', label: 'All', color: '#4169E1' },
  { value: 'checked_in', label: 'On premises', color: '#16a34a' },
  { value: 'checked_out', label: 'Checked out', color: '#64748b' },
  { value: 'denied', label: 'Denied', color: '#ef4444' },
];

const VisitorLogDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // History is session-scoped and the endpoint rejects a missing session_id,
  // so that tab can't fetch until school details have loaded.
  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;

  const [tab, setTab] = useState<TabKey>('today');
  const [visitors, setVisitors] = useState<VisitorRecord[]>([]);
  const [counts, setCounts] = useState<VisitorStatusCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [visitorType, setVisitorType] = useState<string | null>(null);
  const [meetPersonType, setMeetPersonType] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<(Date | null)[] | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<VisitorPagination>({
    current_page: 1,
    per_page: 10,
    total_items: 0,
    total_pages: 1,
  });

  const [viewId, setViewId] = useState<string | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingVisitor, setEditingVisitor] = useState<VisitorRecord | null>(null);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  const startDate = dateRange?.[0] ?? null;
  const endDate = dateRange?.[1] ?? null;

  const fetchVisitors = useCallback(async () => {
    if (tab === 'history' && !currentSessionId) {
      setVisitors([]);
      return;
    }

    setLoading(true);
    try {
      const params: any = { page, limit: pageSize };
      if (status !== 'all') params.status = status;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      if (tab === 'history') {
        params.session_id = currentSessionId;
        if (startDate) params.start_date = toDateParam(startDate);
        if (endDate) params.end_date = toDateParam(endDate);
        if (visitorType) params.visitor_type = visitorType;
        if (meetPersonType) params.meet_person_type = meetPersonType;
      }

      const endpoint = tab === 'today' ? 'api/visitors/today' : 'api/visitors/history';
      const res = await apiClient.get(endpoint, { params });
      const data = res.data;

      if (data.status === 'success') {
        const rows: ApiVisitorLog[] = data.data || [];
        setVisitors(rows.map(mapApiVisitor));
        setPagination(data.pagination);
        // Only /today returns counts; keep the chips from going stale on history.
        setCounts(data.counts ?? EMPTY_COUNTS);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Not loaded',
          detail: data.message || 'The visitor log could not be loaded.',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Could not reach the visitor log.',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    tab,
    page,
    pageSize,
    status,
    debouncedSearch,
    currentSessionId,
    startDate,
    endDate,
    visitorType,
    meetPersonType,
  ]);

  useEffect(() => {
    fetchVisitors();
  }, [fetchVisitors]);

  // Any filter change restarts at page 1 — otherwise you can land on an
  // out-of-range page and see an empty table with results behind it.
  const resetToFirstPage = () => setPage(1);

  const handleTabChange = (next: TabKey) => {
    if (next === tab) return;
    setTab(next);
    setStatus('all');
    setVisitorType(null);
    setMeetPersonType(null);
    setDateRange(null);
    resetToFirstPage();
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const handleCheckOut = async (visitor: VisitorRecord) => {
    setBusyRowId(visitor.id);
    try {
      const res = await apiClient.put(`api/visitors/${visitor.id}/check-out`);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Checked out',
          detail: `${visitor.visitorName} has left.`,
          life: 4000,
        });
        fetchVisitors();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The visitor could not be checked out.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while checking out.',
        life: 5000,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const handleDelete = async (visitor: VisitorRecord) => {
    const confirmed = window.confirm(
      `Delete the visitor record for ${visitor.visitorName}? The ID proof photo is removed too and this can't be undone.`,
    );
    if (!confirmed) return;

    setBusyRowId(visitor.id);
    try {
      const res = await apiClient.delete(`api/visitors/${visitor.id}`);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: 'The visitor record is gone.',
          life: 4000,
        });
        fetchVisitors();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The visitor record could not be deleted.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while deleting.',
        life: 5000,
      });
    } finally {
      setBusyRowId(null);
    }
  };

  const openCreate = () => {
    setFormMode('create');
    setEditingVisitor(null);
    setIsFormOpen(true);
  };

  const openEdit = (visitor: VisitorRecord) => {
    setFormMode('edit');
    setEditingVisitor(visitor);
    setIsViewOpen(false);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingVisitor(null);
    if (formMode === 'create') setPage(1);
    fetchVisitors();
  };

  // ── column templates ─────────────────────────────────────────────────────

  const serialTemplate = (_: VisitorRecord, options: ColumnBodyOptions): JSX.Element => (
    <div style={serialStyle}>{options.rowIndex + (page - 1) * pageSize + 1}</div>
  );

  const visitorTemplate = (row: VisitorRecord): JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {row.photoUrl ? (
        <img src={row.photoUrl} alt="" style={thumbStyle} />
      ) : (
        <div style={{ ...thumbStyle, ...avatarFallbackStyle }}>{getInitials(row.visitorName)}</div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={nameStyle}>{row.visitorName}</div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{row.visitorType}</div>
      </div>
    </div>
  );

  const contactTemplate = (row: VisitorRecord): JSX.Element => (
    <div
      style={{
        fontSize: 13,
        color: row.contactNo ? '#6b7280' : '#c4c9d1',
        fontFamily: 'monospace',
      }}
    >
      {row.contactNo || '—'}
    </div>
  );

  const purposeTemplate = (row: VisitorRecord): JSX.Element => (
    <div style={{ maxWidth: 210 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{row.purpose}</div>
      {row.purposeDetails && (
        <div style={ellipsisStyle} title={row.purposeDetails}>
          {row.purposeDetails}
        </div>
      )}
    </div>
  );

  const meetingTemplate = (row: VisitorRecord): JSX.Element => (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{row.meetPersonName}</div>
      <div style={{ fontSize: 11, color: '#9ca3af' }}>{row.meetPersonType}</div>
    </div>
  );

  const checkInTemplate = (row: VisitorRecord): JSX.Element => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        color: '#1e40af',
        lineHeight: 1.2,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: 6,
          background: 'rgba(59, 130, 246, 0.12)',
          color: '#3b82f6',
          flexShrink: 0,
        }}
      >
        <i className="pi pi-sign-in" style={{ fontSize: 9 }} />
      </span>
      <span style={{ whiteSpace: 'nowrap' }}>{formatTime(row.checkInTime)}</span>
    </div>
  );

  const checkOutTemplate = (row: VisitorRecord): JSX.Element => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        color: row.checkOutTime ? '#64748b' : '#cbd5e1',
        lineHeight: 1.2,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: 6,
          background: row.checkOutTime
            ? 'rgba(100, 116, 139, 0.12)'
            : 'rgba(226, 232, 240, 0.6)',
          color: row.checkOutTime ? '#64748b' : '#cbd5e1',
          flexShrink: 0,
        }}
      >
        <i className="pi pi-sign-out" style={{ fontSize: 9 }} />
      </span>
      <span style={{ whiteSpace: 'nowrap' }}>
        {row.checkOutTime ? formatTime(row.checkOutTime) : '—'}
      </span>
    </div>
  );

  const dateTemplate = (row: VisitorRecord): JSX.Element => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
      {formatVisitDate(row.visitDate)}
    </div>
  );

  const statusTemplate = (row: VisitorRecord): JSX.Element => {
    const meta = STATUS_META[row.status];
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '4px 10px',
          borderRadius: 20,
          fontSize: 11.5,
          fontWeight: 700,
          color: meta.color,
          background: meta.bg,
          border: `1px solid ${meta.border}`,
          whiteSpace: 'nowrap',
        }}
      >
        <i className={meta.icon} style={{ fontSize: 11 }} />
        {meta.label}
      </span>
    );
  };

  const actionTemplate = (row: VisitorRecord): JSX.Element => {
    const busy = busyRowId === row.id;
    return (
      <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-rounded"
          tooltip="View"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#60a5fa', width: 32, height: 32 }}
          disabled={busy}
          onClick={() => {
            setViewId(row.id);
            setIsViewOpen(true);
          }}
        />
        {row.status === 'checked_in' && (
          <Button
            icon="pi pi-sign-out"
            className="p-button-text p-button-rounded"
            tooltip="Check out"
            tooltipOptions={{ position: 'top' }}
            style={{ color: '#16a34a', width: 32, height: 32 }}
            disabled={busy}
            onClick={() => handleCheckOut(row)}
          />
        )}
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-rounded"
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#3b82f6', width: 32, height: 32 }}
          disabled={busy}
          onClick={() => openEdit(row)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-rounded"
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          style={{ color: '#ef4444', width: 32, height: 32 }}
          disabled={busy}
          onClick={() => handleDelete(row)}
        />
      </div>
    );
  };

  // ── empty state copy ─────────────────────────────────────────────────────

  const emptyState = (() => {
    if (tab === 'history' && !currentSessionId)
      return {
        icon: 'pi pi-exclamation-circle',
        title: 'No active session',
        message: 'History is scoped to a session and none is set for this school yet.',
      };
    if (debouncedSearch.trim())
      return {
        icon: 'pi pi-search',
        title: 'No matches',
        message: `Nothing matched "${debouncedSearch}". Try a name, contact number, purpose, or the person being met.`,
      };
    if (status !== 'all')
      return {
        icon: 'pi pi-filter',
        title: 'Nothing in this status',
        message: 'Switch to All to see the rest of the log.',
      };
    if (tab === 'today')
      return {
        icon: 'pi pi-users',
        title: 'No visitors yet today',
        message: 'Check someone in and they will show up here.',
      };
    return {
      icon: 'pi pi-inbox',
      title: 'No records in this range',
      message: 'Widen the dates or clear the filters to see more of the log.',
    };
  })();

  const totalToday = counts.checked_in + counts.checked_out + counts.denied;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          @keyframes slideIn { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:16px 12px!important; border-bottom:1px solid #e5e7eb!important; }
          .p-datatable .p-datatable-tbody > tr > td { padding:14px 12px!important; border-bottom:1px solid #f3f4f6!important; }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .p-button-text:hover { background:#f3f4f6!important; }
          .visitor-toolbar { background:white; border-radius:16px; padding:14px 18px; margin-bottom:16px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); }
          .visitor-tabs { display:inline-flex; gap:4px; background:#f1f5f9; border-radius:12px; padding:4px; }
          .visitor-tab { border:none; background:transparent; padding:7px 18px; border-radius:9px; font-size:13px; font-weight:600; color:#64748b; cursor:pointer; transition:all .15s; }
          .visitor-tab:hover { color:#1e40af; }
          .visitor-tab.active { background:white; color:#1e40af; box-shadow:0 1px 4px rgba(15,23,42,.1); }
          .visitor-search { display:flex; align-items:center; gap:10px; flex:1; min-width:240px; background:#f8fafc; border:1px solid #e5e7eb; border-radius:12px; padding:9px 14px; transition:border-color .2s; }
          .visitor-search:focus-within { border-color:rgba(59,130,246,.45); background:white; }
          .visitor-search input { flex:1; border:none; outline:none; background:transparent; font-size:13px; font-weight:500; color:#1f2937; }
          .visitor-search input::placeholder { color:#9ca3af; font-weight:400; }
          .visitor-clear { background:none; border:none; cursor:pointer; color:#9ca3af; display:flex; padding:2px; }
          .visitor-clear:hover { color:#6b7280; }
          .status-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 13px; border-radius:20px; font-size:12px; font-weight:600; cursor:pointer; border:1px solid #e5e7eb; background:white; color:#4b5563; transition:all .15s; }
          .status-chip:hover { border-color:#93c5fd; }
          .status-chip .chip-count { font-size:11px; font-weight:700; padding:1px 7px; border-radius:10px; background:#f1f5f9; }
        `}</style>

        <GlobalHeaderTitle
          title="Visitor Log"
          subtitle={
            tab === 'today'
              ? `${counts.checked_in} on premises · ${totalToday} today`
              : `${pagination.total_items} records in this range`
          }
          icon="pi pi-id-card"
          buttons={[
            {
              label: 'Refresh',
              icon: 'pi pi-refresh',
              onClick: fetchVisitors,
              outlined: true,
              disabled: loading,
              style: { borderWidth: '2px' },
            },
            {
              label: 'Check in visitor',
              icon: 'pi pi-plus',
              onClick: openCreate,
            },
          ]}
        />

        {/* ── Toolbar ── */}
        <div className="visitor-toolbar">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="visitor-tabs">
              <button
                className={`visitor-tab ${tab === 'today' ? 'active' : ''}`}
                onClick={() => handleTabChange('today')}
              >
                Today
              </button>
              <button
                className={`visitor-tab ${tab === 'history' ? 'active' : ''}`}
                onClick={() => handleTabChange('history')}
              >
                History
              </button>
            </div>

            <div className="visitor-search">
              <i className="pi pi-search" style={{ fontSize: 14, color: '#3b82f6' }} />
              <input
                type="text"
                placeholder="Search by name, contact, purpose, or person being met"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {searchQuery && (
                <button className="visitor-clear" onClick={() => handleSearchChange('')}>
                  <i className="pi pi-times" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              marginTop: 12,
            }}
          >
            {STATUS_FILTERS.map((opt) => {
              const active = status === opt.value;
              const count =
                tab === 'today'
                  ? opt.value === 'all'
                    ? totalToday
                    : counts[opt.value as VisitorStatus]
                  : null;
              return (
                <button
                  key={opt.value}
                  className="status-chip"
                  onClick={() => {
                    setStatus(opt.value);
                    resetToFirstPage();
                  }}
                  style={
                    active
                      ? { background: opt.color, borderColor: opt.color, color: 'white' }
                      : undefined
                  }
                >
                  {opt.label}
                  {count !== null && (
                    <span
                      className="chip-count"
                      style={
                        active
                          ? { background: 'rgba(255,255,255,.25)', color: 'white' }
                          : { color: opt.color }
                      }
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {tab === 'history' && (
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid #f1f5f9',
              }}
            >
              <div style={{ minWidth: 240, maxWidth: 320, flex: '0 1 300px' }}>
                <GlobalDateField
                  label="Date range"
                  value={dateRange}
                  onChange={(val) => {
                    setDateRange(val as (Date | null)[] | null);
                    resetToFirstPage();
                  }}
                  selectionMode="range"
                  placeholder="Select date range"
                  dateFormat="dd M yy"
                  icon="pi pi-calendar"
                  showButtonBar
                  fullWidth
                />
              </div>

              <div style={{ minWidth: 160, maxWidth: 190, flex: '0 1 170px' }}>
                <GlobalDropdownField
                  label="Visitor type"
                  value={visitorType}
                  onChange={(val) => {
                    setVisitorType(val);
                    resetToFirstPage();
                  }}
                  options={VISITOR_TYPE_OPTIONS.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  placeholder="All types"
                  showClear
                  fullWidth
                />
              </div>

              <div style={{ minWidth: 160, maxWidth: 190, flex: '0 1 170px' }}>
                <GlobalDropdownField
                  label="Meeting with"
                  value={meetPersonType}
                  onChange={(val) => {
                    setMeetPersonType(val);
                    resetToFirstPage();
                  }}
                  options={MEET_PERSON_TYPE_OPTIONS.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  placeholder="Anyone"
                  showClear
                  fullWidth
                />
              </div>

              {(dateRange || visitorType || meetPersonType) && (
                <Button
                  label="Clear filters"
                  icon="pi pi-filter-slash"
                  className="p-button-text p-button-sm"
                  style={{ marginBottom: 2 }}
                  onClick={() => {
                    setDateRange(null);
                    setVisitorType(null);
                    setMeetPersonType(null);
                    resetToFirstPage();
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* ── Table / Skeleton ── */}
        {loading ? (
          <VisitorLogSkeletonLoader />
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {visitors.length > 0 && (
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  Showing {(page - 1) * pageSize + 1}–
                  {Math.min(page * pageSize, pagination.total_items)} of {pagination.total_items}{' '}
                  {tab === 'today' ? "of today's visitors" : 'records'}
                </span>
                <div
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: 'rgba(65,105,225,.08)',
                    border: '1px solid rgba(65,105,225,.15)',
                    fontSize: 13,
                    color: '#4169E1',
                    fontWeight: 600,
                  }}
                >
                  Page {pagination.current_page} of {pagination.total_pages}
                </div>
              </div>
            )}

            <DataTable
              value={visitors}
              loading={false}
              emptyMessage={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    minHeight: 360,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                      borderRadius: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 20,
                      boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                    }}
                  >
                    <i className={emptyState.icon} style={{ fontSize: 25, color: 'white' }} />
                  </div>
                  <h3
                    style={{
                      margin: '0 0 8px',
                      color: '#1e40af',
                      fontSize: 20,
                      fontWeight: 700,
                      textAlign: 'center',
                    }}
                  >
                    {emptyState.title}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      color: '#64748b',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'center',
                      maxWidth: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {emptyState.message}
                  </p>
                </div>
              }
            >
              <Column header="#" body={serialTemplate} style={{ width: 52 }} />
              <Column header="Visitor" body={visitorTemplate} />
              <Column header="Contact" body={contactTemplate} style={{ width: 130 }} />
              <Column header="Purpose" body={purposeTemplate} />
              <Column header="Meeting" body={meetingTemplate} />
              {tab === 'history' && (
                <Column header="Date" body={dateTemplate} style={{ width: 120 }} />
              )}
              <Column header="Check in" body={checkInTemplate} style={{ width: 110 }} />
              <Column header="Check out" body={checkOutTemplate} style={{ width: 110 }} />
              <Column header="Status" body={statusTemplate} style={{ width: 130 }} />
              <Column header="Actions" body={actionTemplate} style={{ width: 150 }} />
            </DataTable>

            {pagination.total_pages > 1 && (
              <CustomPaginator
                page={pagination.current_page}
                totalPages={pagination.total_pages}
                totalRecords={pagination.total_items}
                pageSize={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                }}
                showInfoBar={false}
              />
            )}
          </div>
        )}
      </div>

      <ViewVisitorModal
        open={isViewOpen}
        visitorId={viewId}
        onClose={() => setIsViewOpen(false)}
        onChanged={fetchVisitors}
        onEdit={openEdit}
      />

      <VisitorFormModal
        open={isFormOpen}
        mode={formMode}
        initialRecord={editingVisitor}
        onClose={() => {
          setIsFormOpen(false);
          setEditingVisitor(null);
        }}
        onSuccess={handleFormSuccess}
      />
    </>
  );
};

const serialStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  background: '#f9fafb',
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 12,
  color: '#6b7280',
  margin: '0 auto',
};

const thumbStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  objectFit: 'cover',
  border: '1px solid #e5e7eb',
  flexShrink: 0,
};

const avatarFallbackStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg,#4169E1,#5B7CE6)',
  color: 'white',
  fontSize: 13,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
};

const nameStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 13,
  color: '#1f2937',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const ellipsisStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#9ca3af',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default VisitorLogDashboard;